import { useEffect, useRef, useState } from "react";

import { getLots } from "services/sosInventoryService/sosApi";
import { dateToSosISODateTime } from "services/utility/dates";

/**
 * @name    useItemLocationLots
 *
 * @summary returns an array of lots, optionally filtered by item, location,
 *          and bin
 *
 * @param   itemId (number)
 *          locationName (string) name property of a location reference
 *            object
 *          dateTime (date) date/time at which onhand quantities should be
 *            calculated
 *          lotTracking (boolean) is lot tracking enabled for this item?
 *          binId (number, optional)
 *          lotId (number, optional) the persisted lot id; frozen at mount so
 *            changes to the lot during editing do not re-trigger the fetch
 *
 * @returns array of lots; while waiting for API response, returns null
 */
export function useItemLocationLots(params, date, lotTracking) {
  const [lots, setLots] = useState(null);
  const { itemId, location, binId, includeZeroOnhandLotsInBin, lotId } = params;
  const persistedLotId = useRef(lotId);

  useEffect(() => {
    async function _getLots() {
      const lots = await getLots({
        itemId,
        location,
        binId,
        includeZeroOnhandLotsInBin,
        ...(persistedLotId.current && { includeId: persistedLotId.current }),
        dt: dateToSosISODateTime(date),
      });
      setLots(lots);
    }

    if (Boolean(itemId && location && date && lotTracking)) {
      setLots(null);
      _getLots();
    } else {
      setLots([]);
    }
  }, [itemId, location, binId, includeZeroOnhandLotsInBin, date, lotTracking]);

  return lots;
}
